import { List, is } from "immutable";
import * as React from "react";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import * as Forms from "../../../forms";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";
import { ReactHelper } from "../../../utils";
import { EditForm } from "./EditForm";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultStatePropsMap(state, ownProps),
    events: state.adminEvents.events,
    loadingEvent: state.adminEvents.loadingEvent,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadEvent: (eventId: number) => dispatch(Actions.AdminEvent.loadEvent(eventId)),
    removeEvent: (evt: M.Event.Event) => dispatch(Actions.AdminEvent.removeEvent(evt)),
    toggleLoading: (eventId: number, loading: boolean) => dispatch(Actions.AdminEvent.toggleLoadingEvent(eventId, loading)),
});

type OwnProps = RouteComponentProps<{
    id: string;
}> & {};

type ConnectedState = Comp.Base.DefaultConnectedState & {
    events: List<M.Event.Event>;
    loadingEvent: M.Generic.ItemLoadingState;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadEvent: (eventId: number) => void;
    removeEvent: (evt: M.Event.Event) => void;
    toggleLoading: (eventId: number, loading: boolean) => void;
};

interface OwnState {
    event?: M.Event.Event;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class EditComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    private _confirmDelete: Comp.Modal.ConfirmModal | null = null;
    private form: EditForm | null = null;

    public constructor(props: any) {
        super(props);
        this.state = {
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
        const eventId = this.getEventId();
        await this.loadEventFromState(true);

        if (eventId > 0)
            this.props.loadEvent(eventId);
    }

    public componentDidUpdate(prevProps: Readonly<CombinedProps>) {
        if (!is(prevProps.events, this.props.events))
            this.loadEventFromState();
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        const evt = this.state.event;
        const eventId = this.getEventId();
        const loading = !!this.props.loadingEvent[eventId];
        const eventListLink = this.lastPathIsFromListView()
            ? <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); window.history.back(); }}>Events</a>
            : <Link to={Paths.AdminEvent.List}>Events</Link>;

        const cancelLink = this.lastPathIsFromListView()
            ? <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); window.history.back(); }}>Cancel</a>
            : <Link to={Paths.AdminEvent.List}>Cancel</Link>;

        const cancelWrap = <Comp.Generic.WrapperLess>
            {cancelLink}
            {eventId > 0
                ? <span className="btn btn-link mute" onClick={() => this.toggleDeleteConfirmation()} style={{ float: "right" }}>
                    <Comp.Generic.Icon icon="times" /> Delete
                </span>
                : null}
        </Comp.Generic.WrapperLess>;

        return <Comp.Page.AdminPage title={"Events"}>

            {this.getErrorAlert()}

            <Comp.Generic.LoadingSpinner show={loading} />

            {eventId > 0
                ? <Comp.Modal.ConfirmModal
                    headerClassName="warning"
                    onProceed={() => this.handleDelete()}
                    title="Delete event"
                    ref={(ref) => this._confirmDelete = ref}
                >
                    <p>
                        Are you sure you want to delete this event?
                        Any associated registrants or other data will also be deleted.
                    </p>
                </Comp.Modal.ConfirmModal>
                : null}

            <h1>
                {eventListLink} &raquo;{" "}
                {eventId === 0 ? "Add new event" : `Edit ${evt ? evt.title : "event"}`}
            </h1>

            <EditForm
                api={this.props.api}
                cancelLink={cancelWrap}
                onSubmit={(model, body) => this.onSubmit(model, body)}
                ref={(ref: any) => this.form = ref}
            />

        </Comp.Page.AdminPage>;
    }

    private getEventId(props?: Readonly<CombinedProps>) {
        return parseInt(props
            ? props.match.params.id
            : this.props.match.params.id, 10);
    }

    private async handleDelete() {
        if (this._confirmDelete === null)
            return;

        const event = this._confirmDelete.getRef() as M.Event.Event;
        if (event) {
            const result = await this.props.api.delete(Api.endpoints.admin.event.event.withUrlParams({ eventId: event.id }));

            if (result.isSuccess) {
                this.props.removeEvent(event);
                this.props.notifySuccess(`Event <strong>${event.title}</strong> has been successfully deleted`);
                this.props.goto(Paths.AdminEvent.List);
            }
            else {
                this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
            }

            if (this._confirmDelete)
                this._confirmDelete.close();
        }
    }

    private lastPathIsFromListView() {
        const lastPath = this.props.lastPath;
        const path = Paths.AdminEvent.List;
        return lastPath && (lastPath === path || lastPath.indexOf(`${path}?`) === 0);
    }

    private async loadEventFromState(isInitialCheck?: boolean) {
        const eventId = this.getEventId();
        const event = this.props.events.find((x: M.Event.Event) => x.id === eventId);

        if (event) {
            await this.setStateAsync({ ...this.state, event });
            if (this.form)
                this.form.setFormData(event);
        }

        if (eventId > 0 && !event && isInitialCheck)
            this.props.toggleLoading(eventId, true);

        if (eventId > 0 && !event && !isInitialCheck) {
            this.props.goto(Paths.AdminEvent.List);
        }
    }

    private async onSubmit(model: Forms.AdminEvent.EventData, body: any) {
        if (this.form)
            this.form.toggleProcessing(true);

        const result = await this.props.api.post(Api.endpoints.admin.event.event.withUrlParams({ eventId: model.id }), {
            body: JSON.stringify(body),
        });

        if (!this._isMounted || !this.form)
            return;

        this.form.toggleProcessing(false);

        if (result.isSuccess) {
            this.props.notifySuccess(`Event <strong>${model.title}</strong><br />has been successfully saved.`);
            this.props.goto(Paths.AdminEvent.List);
        }
        else {
            this.form.setFormErrors(M.Result.Result.errorsOrMessage(result), undefined, true);
        }
    }

    private toggleDeleteConfirmation() {
        if (this._confirmDelete !== null)
            this._confirmDelete.show(this.state.event);
    }
}

export const Edit: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(EditComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/adminEvent/edit/Edit.tsx